import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Bell, Shield, ChevronRight, ChevronLeft, Palette, Sun, Moon, Monitor, Settings } from 'lucide-react';
import UserProfile from '../User/UserProfile';
import SimpleNotificationPreferences from '../Notifications/SimpleNotificationPreferences';
import SecurityTab from './SecurityTab';
import PageHeader from '../Shared/PageHeader';
import userProfileService from '../../services/userProfileService';
import { useTheme } from '../../contexts/ThemeContext';

const ThemeOption = ({ value, label, icon: Icon, active, onClick }) => (
  <button
    onClick={() => onClick(value)}
    className={`
      relative flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-colors duration-150
      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-800
      ${active
        ? 'border-primary-600 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
        : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
      }
    `}
  >
    <div className={`
      flex h-10 w-10 items-center justify-center rounded-lg transition-colors duration-150
      ${active
        ? 'bg-primary-600 text-white dark:bg-primary-500'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
      }
    `}>
      <Icon className="h-[18px] w-[18px]" />
    </div>
    <span className={`text-sm font-medium transition-colors duration-150 ${active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300'}`}>
      {label}
    </span>
    {active && (
      <motion.div
        layoutId="theme-indicator"
        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 ring-2 ring-white dark:bg-primary-500 dark:ring-gray-800"
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </motion.div>
    )}
  </button>
);

const AppearanceTab = () => {
  const { theme, setTheme, resolvedTheme } = useTheme();

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Theme</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Choose how FlexCOA looks to you</p>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-md">
        <ThemeOption value="system" label="System" icon={Monitor} active={theme === 'system'} onClick={setTheme} />
        <ThemeOption value="light" label="Light" icon={Sun} active={theme === 'light'} onClick={setTheme} />
        <ThemeOption value="dark" label="Dark" icon={Moon} active={theme === 'dark'} onClick={setTheme} />
      </div>

      {theme === 'system' && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="text-xs text-gray-500 dark:text-gray-400"
        >
          Currently using <span className="font-medium">{resolvedTheme}</span> based on your system settings
        </motion.p>
      )}
    </div>
  );
};

const ProfilePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [userProfile, setUserProfile] = useState(null);

  const tabs = [
    { id: 'account', label: 'Account', icon: User, description: 'Personal information' },
    { id: 'security', label: 'Security', icon: Shield, description: 'Password & MFA' },
    { id: 'notifications', label: 'Notifications', icon: Bell, description: 'Alert preferences' },
    { id: 'appearance', label: 'Appearance', icon: Palette, description: 'Theme & display' },
  ];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    
    const validTabs = ['account', 'security', 'notifications', 'appearance'];
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Load user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userData = await userProfileService.getUserProfile();
        setUserProfile(userData);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Single-study users get no Studies menu item and /profile has no breadcrumbs,
  // so this is their only way back. Fall back to /studies on a direct landing.
  const handleBack = () => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate('/studies');
    }
  };

  return (
    <div>
      <div className="max-w-6xl mx-auto">
        <button
          onClick={handleBack}
          className="back-link"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Back</span>
        </button>

        {/* Page Header */}
        <PageHeader
          icon={Settings}
          title="Settings"
          subtitle="Manage your account, security, and preferences"
        />

        {/* Main Content Card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="card overflow-hidden"
        >
          {/* Tab Navigation */}
          <div className="flex flex-col lg:flex-row">
            {/* Tab Sidebar */}
            <div className="border-b border-gray-200/80 bg-gray-50 lg:w-60 lg:border-b-0 lg:border-r dark:border-white/[0.07] dark:bg-gray-900">
              <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" aria-label="Tabs">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        relative flex items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors duration-150
                        min-w-[120px] lg:min-w-0 lg:w-full
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60
                        ${isActive 
                          ? 'border-gray-200/80 bg-white text-gray-900 shadow-xs dark:border-white/10 dark:bg-gray-800 dark:text-gray-100' 
                          : 'border-transparent text-gray-600 hover:bg-white/70 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/[0.04] dark:hover:text-gray-100'
                        }
                      `}
                    >
                      <Icon className={`h-[18px] w-[18px] flex-shrink-0 ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium">{tab.label}</span>
                        <span className="hidden text-xs text-gray-500 dark:text-gray-400 lg:block">{tab.description}</span>
                      </div>
                      {isActive && (
                        <ChevronRight className="hidden h-4 w-4 text-gray-400 dark:text-gray-500 lg:block" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="flex-1 min-w-0 min-h-[500px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="h-full"
                >
                  {activeTab === 'account' && (
                    <UserProfile embedded={true} />
                  )}

                  {activeTab === 'security' && (
                    <div className="p-6">
                      <SecurityTab userProfile={userProfile} embedded={true} />
                    </div>
                  )}

                  {activeTab === 'notifications' && (
                    <div className="p-6">
                      <SimpleNotificationPreferences embedded={true} />
                    </div>
                  )}
                  {activeTab === 'appearance' && (
                    <AppearanceTab />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
